import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import {
  listInstanceAccess,
  grantInstanceAccess,
  removeInstanceAccess,
} from '../../../../services';
import { validateApiResponse } from '../../../../shared/utils/apiResponse';

/** Per-instance access control (CT-4191): entries, directory, grant/remove. */
export function useInstanceAccess(instanceId) {
  const { t } = useTranslation('admin-studio');
  const [entries, setEntries] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [workspaceTotal, setWorkspaceTotal] = useState(0);
  const [reachCount, setReachCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchIdRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(
    async ({ silent = false } = {}) => {
      const fetchId = ++fetchIdRef.current;
      if (!silent) setIsLoading(true);
      setError(null);
      try {
        const body = validateApiResponse(await listInstanceAccess(instanceId), t('common.requestFailed')).body;
        if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
        setEntries(body?.entries ?? []);
        setDirectory(body?.directory ?? []);
        setWorkspaceTotal(body?.workspace_total ?? 0);
        setReachCount(body?.reach_count ?? 0);
      } catch (err) {
        if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
        setError(err?.message ?? t('common.requestFailed'));
      } finally {
        if (!silent && mountedRef.current && fetchId === fetchIdRef.current) setIsLoading(false);
      }
    },
    [instanceId, t],
  );

  useEffect(() => {
    load();
  }, [load]);

  const grant = useCallback(
    async (entity, level) => {
      try {
        await grantInstanceAccess(instanceId, entity, level);
        load({ silent: true });
      } catch (err) {
        if (mountedRef.current) setError(err?.message ?? t('common.requestFailed'));
      }
    },
    [instanceId, load, t],
  );

  const remove = useCallback(
    async (entryId) => {
      try {
        await removeInstanceAccess(instanceId, entryId);
        load({ silent: true });
      } catch (err) {
        if (mountedRef.current) setError(err?.message ?? t('common.requestFailed'));
      }
    },
    [instanceId, load, t],
  );

  return useMemo(
    () => ({ entries, directory, workspaceTotal, reachCount, isLoading, error, grant, remove }),
    [entries, directory, workspaceTotal, reachCount, isLoading, error, grant, remove],
  );
}
